import Breadcrumb from "components/Common/Breadcrumb";
import DashboardLoader from "components/Loader/DashboardLoader";
import React, { useState } from "react";
import { Row, Col, Card, CardBody, Button, Badge } from "reactstrap";
import { useGetUserQuery, useUpdateUserMutation } from "store/features/users/userManagementApi";
import { toast } from "sonner";
import { useSelector } from "react-redux";
import { selectUser } from "store/features/auth/authSlice";
import EditProfileModal from "components/Modals/EditProfileModal";

const UserProfile = () => {
  const userDetails = useSelector(selectUser);

  const { data: user, isLoading, refetch } = useGetUserQuery(userDetails?.id);
  const [editModalOpen, setEditModalOpen] = useState(false);

  const toggleEditModal = () => setEditModalOpen(!editModalOpen);

  const handleProfileUpdate = () => {
    refetch();
    toggleEditModal();
    toast.success("Profile updated successfully");
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    return `https://qwikbistro.shop${imagePath}`;
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      'admin': { color: 'danger', text: 'Admin' },
      'developer': { color: 'primary', text: 'Developer' },
      'client': { color: 'success', text: 'Client' },
      'manager': { color: 'warning', text: 'Manager' }
    };
    const config = roleConfig[role] || { color: 'secondary', text: role };
    return config;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not set";
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return <DashboardLoader />;
  }

  if (!user) {
    return (
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumb title="Profile" breadcrumbItem="My Profile" />
          <div className="text-center py-5">
            <i className="fas fa-user-slash display-1 text-muted"></i>
            <h4 className="mt-3">User not found</h4>
            <p className="text-muted">Please check if you're logged in correctly.</p>
          </div>
        </div>
      </div>
    );
  }

  const roleConfig = getRoleBadge(user.role);
  const imageUrl = getImageUrl(user.image);

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumb title="Profile" breadcrumbItem="My Profile" />

        <Row className="justify-content-center">
          <Col lg={8}>
            <Card>
              <CardBody>

                <div className="d-flex justify-content-end">
                  <Button color="primary" onClick={toggleEditModal}>
                    <i className="fas fa-edit me-2"></i>Edit Profile
                  </Button>

                </div>
                {/* Profile Header */}
                <div className="text-center mb-5">
                  <div className="position-relative d-inline-block">
                    {user?.image ? (
                      <img
                        src={user?.image}
                        alt={user.nickname}
                        className="rounded-circle"
                        style={{
                          width: "150px",
                          height: "150px",
                          objectFit: "cover",
                          border: "4px solid #fff",
                          boxShadow: "0 0 20px rgba(0,0,0,0.1)"
                        }}
                      />
                    ) : (
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center bg-light"
                        style={{
                          width: "150px",
                          height: "150px",
                          border: "4px solid #fff",
                          boxShadow: "0 0 20px rgba(0,0,0,0.1)"
                        }}
                      >
                        <i className="fas fa-user display-4 text-muted"></i>
                      </div>
                    )}
                    <Badge
                      color={roleConfig.color}
                      className="position-absolute bottom-0 end-0 rounded-pill"
                    >
                      {roleConfig.text}
                    </Badge>
                  </div>
                  <h3 className="mt-3 mb-1">{user.nickname}</h3>
                  <p className="text-muted mb-3">{user.personal_email}</p>

                </div>

                <Row>
                  {/* Personal Information */}
                  <Col lg={6}>
                    <Card className="border">
                      <CardBody>
                        <h5 className="card-title border-bottom pb-2 mb-3">
                          <i className="fas fa-user me-2 text-primary"></i>
                          Personal Information
                        </h5>
                        <div className="mb-3">
                          <label className="form-label fw-semibold text-muted mb-1">Name</label>
                          <p className="mb-0">{user.nickname}</p>
                        </div>
                        <div className="mb-3">
                          <label className="form-label fw-semibold text-muted mb-1">Email</label>
                          <p className="mb-0">{user.personal_email}</p>
                        </div>
                        <div className="mb-3">
                          <label className="form-label fw-semibold text-muted mb-1">Phone</label>
                          <p className="mb-0">{user.phone || "Not set"}</p>
                        </div>
                        <div className="mb-3">
                          <label className="form-label fw-semibold text-muted mb-1">Gender</label>
                          <p className="mb-0">{user.gender ? user.gender.charAt(0).toUpperCase() + user.gender.slice(1) : "Not set"}</p>
                        </div>
                        <div className="mb-3">
                          <label className="form-label fw-semibold text-muted mb-1">Address</label>
                          <p className="mb-0">{user.address || "Not set"}</p>
                        </div>
                        {/* <div className="mb-3">
                          <label className="form-label fw-semibold text-muted mb-1">Date of Birth</label>
                          <p className="mb-0">{formatDate(user.date_of_birth)}</p>
                        </div> */}



                      </CardBody>
                    </Card>
                  </Col>

                  {/* Professional Information */}
                  <Col lg={6}>
                    <Card className="border">
                      <CardBody>
                        <h5 className="card-title border-bottom pb-2 mb-3">
                          <i className="fas fa-briefcase me-2 text-success"></i>
                          Professional Information
                        </h5>
                        <div className="mb-3">
                          <label className="form-label fw-semibold text-muted mb-1">Position</label>
                          <p className="mb-0">{user.position || "Not set"}</p>
                        </div>
                        <div className="mb-3">
                          <label className="form-label fw-semibold text-muted mb-1">Employee ID</label>
                          <p className="mb-0">{user.emp_id || "Not set"}</p>
                        </div>
                        {/* <div className="mb-3">
                          <label className="form-label fw-semibold text-muted mb-1">Official Email</label>
                          <p className="mb-0">{user.official_email || "Not set"}</p>
                        </div> */}
                        <div className="mb-3">
                          <label className="form-label fw-semibold text-muted mb-1">Experience</label>
                          <p className="mb-0">{user.experience_years ? `${user.experience_years} years` : "Not set"}</p>
                        </div>
                        <div className="mb-3">
                          <label className="form-label fw-semibold text-muted mb-1">Joining Date</label>
                          <p className="mb-0">{formatDate(user.joining_date)}</p>
                        </div>
                      </CardBody>
                    </Card>
                  </Col>

                  {/* Additional Information */}
                  {/* <Col lg={12} className="mt-3">
                    <Card className="border">
                      <CardBody>
                        <h5 className="card-title border-bottom pb-2 mb-3">
                          <i className="fas fa-info-circle me-2 text-info"></i>
                          Additional Information
                        </h5>
                        <Row>
                          <Col lg={6}>
                            <div className="mb-3">
                              <label className="form-label fw-semibold text-muted mb-1">Address</label>
                              <p className="mb-0">{user.address || "Not set"}</p>
                            </div>
                          </Col>
                          <Col lg={6}>
                            <div className="mb-3">
                              <label className="form-label fw-semibold text-muted mb-1">City</label>
                              <p className="mb-0">{user.city || "Not set"}</p>
                            </div>
                          </Col>
                          <Col lg={6}>
                            <div className="mb-3">
                              <label className="form-label fw-semibold text-muted mb-1">Blood Group</label>
                              <p className="mb-0">{user.blood_group || "Not set"}</p>
                            </div>
                          </Col>
                          <Col lg={6}>
                            <div className="mb-3">
                              <label className="form-label fw-semibold text-muted mb-1">Member Since</label>
                              <p className="mb-0">{formatDate(user.joining_date)}</p>
                            </div>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>
                  </Col> */}
                </Row>

                {/* Emergency Contact */}
                {(user.alt_phone || user.emergency_phone) && (
                  <Col lg={12} className="mt-3">
                    <Card className="border">
                      <CardBody>
                        <h5 className="card-title border-bottom pb-2 mb-3">
                          <i className="fas fa-phone-alt me-2 text-warning"></i>
                          Emergency Contact
                        </h5>
                        <Row>
                          {user.alt_phone && (
                            <Col lg={6}>
                              <div className="mb-3">
                                <label className="form-label fw-semibold text-muted mb-1">Alternative Phone</label>
                                <p className="mb-0">{user.alt_phone}</p>
                              </div>
                            </Col>
                          )}
                          {user.emergency_phone && (
                            <Col lg={6}>
                              <div className="mb-3">
                                <label className="form-label fw-semibold text-muted mb-1">Emergency Phone</label>
                                <p className="mb-0">{user.emergency_phone}</p>
                              </div>
                            </Col>
                          )}
                        </Row>
                      </CardBody>
                    </Card>
                  </Col>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Edit Profile Modal */}
        {user && (
          <EditProfileModal
            isOpen={editModalOpen}
            toggle={toggleEditModal}
            user={user}
            onProfileUpdate={handleProfileUpdate}
          />
        )}
      </div>
    </div>
  );
};

export default UserProfile;

// import PropTypes from "prop-types";
// import React, { useState, useEffect } from "react";
// import {
//   Container,
//   Row,
//   Col,
//   Card,
//   Alert,
//   CardBody,
//   Button,
//   Form,
//   FormFeedback,
//   Label,
//   Input,
// } from "reactstrap";

// // Formik validation
// import * as Yup from "yup";
// import { useFormik } from "formik";

// // Redux
// import { connect, useDispatch } from "react-redux";
// import withRouter from "components/Common/withRouter";

// //Import Breadcrumb
// import Breadcrumb from "../../components/Common/Breadcrumb";

// import avatar from "../../assets/images/users/user-4.jpg";
// // actions
// import { editProfile, resetProfileFlag } from "../../store/actions";

// const UserProfile = props => {
//   const dispatch = useDispatch();

//   const [email, setemail] = useState("");
//   const [name, setname] = useState("");
//   const [idx, setidx] = useState(1);

//   useEffect(() => {
//     if (localStorage.getItem("authUser")) {
//       const obj = JSON.parse(localStorage.getItem("authUser"));
//       if (process.env.REACT_APP_DEFAULTAUTH === "firebase") {
//         setname(obj.displayName);
//         setemail(obj.email);
//         setidx(obj.uid || 1);
//       } else if (
//         process.env.REACT_APP_DEFAULTAUTH === "fake" ||
//         process.env.REACT_APP_DEFAULTAUTH === "jwt"
//       ) {
//         setname(obj.username);
//         setemail(obj.email);
//         setidx(obj.uid || 1);
//       }
//       setTimeout(() => {
//         props.resetProfileFlag();
//       }, 3000);
//     }
//   }, [props.success]);

//   const validation = useFormik({
//     // enableReinitialize : use this flag when initial values needs to be changed
//     enableReinitialize: true,

//     initialValues: {
//       username: name || "",
//       idx: idx || "",
//     },
//     validationSchema: Yup.object({
//       username: Yup.string().required("Please Enter Your User Name"),
//     }),
//     onSubmit: values => {
//       dispatch(editProfile(values));
//     },
//   });

//   document.title = "Profile | Veltrix - React Admin & Dashboard Template";
//   return (
//     <React.Fragment>
//       <div className="page-content">
//         <Container fluid>
//           {/* Render Breadcrumb */}
//           <Breadcrumb title="Veltrix" breadcrumbItem="Profile" />

//           <Row>
//             <Col lg="12">
//               {props.error && props.error ? (
//                 <Alert color="danger">{props.error}</Alert>
//               ) : null}
//               {props.success ? (
//                 <Alert color="success">{props.success}</Alert>
//               ) : null}

//               <Card>
//                 <CardBody>
//                   <div className="d-flex">
//                     <div className="mx-3">
//                       <img
//                         src={avatar}
//                         alt=""
//                         className="avatar-md rounded-circle img-thumbnail"
//                       />
//                     </div>
//                     <div className="align-self-center flex-1">
//                       <div className="text-muted">
//                         <h5>{name}</h5>
//                         <p className="mb-1">{email}</p>
//                         <p className="mb-0">Id no: #{idx}</p>
//                       </div>
//                     </div>
//                   </div>
//                 </CardBody>
//               </Card>
//             </Col>
//           </Row>

//           <h4 className="card-title mb-4">Change User Name</h4>

//           <Card>
//             <CardBody>
//               <Form
//                 className="form-horizontal"
//                 onSubmit={e => {
//                   e.preventDefault();
//                   validation.handleSubmit();
//                   return false;
//                 }}
//               >
//                 <div className="form-group">
//                   <Label className="form-label">User Name</Label>
//                   <Input
//                     name="username"
//                     className="form-control"
//                     placeholder="Enter User Name"
//                     type="text"
//                     onChange={validation.handleChange}
//                     onBlur={validation.handleBlur}
//                     value={validation.values.username || ""}
//                     invalid={
//                       validation.touched.username && validation.errors.username
//                         ? true
//                         : false
//                     }
//                   />
//                   {validation.touched.username && validation.errors.username ? (
//                     <FormFeedback type="invalid">
//                       {validation.errors.username}
//                     </FormFeedback>
//                   ) : null}
//                   <Input name="idx" value={idx} type="hidden" />
//                 </div>
//                 <div className="text-center mt-4">
//                   <Button type="submit" color="danger">
//                     Edit User Name
//                   </Button>
//                 </div>
//               </Form>
//             </CardBody>
//           </Card>
//         </Container>
//       </div>
//     </React.Fragment>
//   );
// };

// UserProfile.propTypes = {
//   editProfile: PropTypes.func,
//   error: PropTypes.any,
//   success: PropTypes.any,
// };

// const mapStatetoProps = state => {
//   const { error, success } = state.Profile;
//   return { error, success };
// };

// export default withRouter(
//   connect(mapStatetoProps, { editProfile, resetProfileFlag })(UserProfile)
// );
